import React, { forwardRef, useState } from 'react'
import { ReactComponent as RefineIcon } from '@assets/images/Icons/Refine.svg'
import { ReactComponent as WriteAiIcon } from '@assets/images/Icons/Write_ai.svg'
import { ReactComponent as DraftsIcon } from '@assets/images/Icons/Drafts.svg'
import { ReactComponent as ArrowRightIcon } from '@assets/images/Icons/Arrow_right.svg'
import { ReactComponent as ArrowDownIcon } from '@assets/images/Icons/Arrow_down.svg'
import { ReactComponent as ArrowDeclinedIcon } from '@assets/images/Icons/Arrow_declined.svg'
import { ReactComponent as CopyIcon } from '@assets/images/Icons/Copy.svg'
import { ReactComponent as CloseIcon } from '@assets/images/Icons/multiply_outline.svg'
import { ReactComponent as ConciseIcon } from '@assets/images/RichTextEditor/ai-concise.svg'
import { ReactComponent as DetailedIcon } from '@assets/images/RichTextEditor/ai-detailed.svg'
import { ReactComponent as ToneIcon } from '@assets/images/RichTextEditor/ai-tone.svg'
import { ReactComponent as RegenerateIcon } from '@assets/images/RichTextEditor/ai-regenerate.svg'
import {
  RICH_TEXT_EDITOR_AI_ACTION_LABELS,
  RICH_TEXT_EDITOR_AI_ACTIONS,
  RICH_TEXT_EDITOR_AI_RESULT_ACTION_LABELS,
  RICH_TEXT_EDITOR_AI_RESULT_ACTIONS,
  RICH_TEXT_EDITOR_AI_TONE_LABELS,
  RICH_TEXT_EDITOR_AI_TONES,
  type RichTextEditorAiActionId,
  type RichTextEditorAiResultActionId,
  type RichTextEditorAiToneId,
} from './RichTextEditor.types'

type IconComponent = React.FunctionComponent<React.SVGProps<SVGSVGElement>>

const AI_ACTION_ICONS: Record<RichTextEditorAiActionId, IconComponent> = {
  fixGrammar: RefineIcon,
  improveWriting: WriteAiIcon,
  moreConcise: ConciseIcon,
  moreDetailed: DetailedIcon,
  summarize: DraftsIcon,
  changeTone: ToneIcon,
}

const AI_RESULT_ACTION_ICONS: Record<
  RichTextEditorAiResultActionId,
  IconComponent
> = {
  regenerate: RegenerateIcon,
  replace: ArrowDownIcon,
  insert: ArrowDeclinedIcon,
  copy: CopyIcon,
  cancel: CloseIcon,
}

export type RichTextEditorAiMenuProps = {
  /** When set, the result menu replaces the assist menu. */
  result?: string | null
  style?: React.CSSProperties
  onAction: (actionId: RichTextEditorAiActionId) => void
  onToneSelect: (toneId: RichTextEditorAiToneId) => void
  onResultAction: (
    actionId: RichTextEditorAiResultActionId,
    result: string,
  ) => void
}

const RichTextEditorAiMenu = forwardRef<
  HTMLDivElement,
  RichTextEditorAiMenuProps
>(({ result, style, onAction, onToneSelect, onResultAction }, ref) => {
  const [toneMenuOpen, setToneMenuOpen] = useState(false)

  if (result) {
    return (
      <div
        ref={ref}
        className="ds-rich-text-editor__ai-menu ds-rich-text-editor__ai-menu--result"
        style={style}
        role="menu"
        aria-label="AI result actions"
      >
        <p className="ds-rich-text-editor__ai-result-text">{`\u201c${result}\u201d`}</p>
        {RICH_TEXT_EDITOR_AI_RESULT_ACTIONS.map((actionId) => {
          const Icon = AI_RESULT_ACTION_ICONS[actionId]

          return (
            <button
              key={actionId}
              type="button"
              className="ds-rich-text-editor__ai-item"
              role="menuitem"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onResultAction(actionId, result)}
            >
              <Icon
                className="ds-rich-text-editor__ai-item-icon"
                width={16}
                height={16}
                aria-hidden="true"
              />
              <span>{RICH_TEXT_EDITOR_AI_RESULT_ACTION_LABELS[actionId]}</span>
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className="ds-rich-text-editor__ai-menu"
      style={style}
      role="menu"
      aria-label="AI assist"
    >
      {RICH_TEXT_EDITOR_AI_ACTIONS.map((actionId) => {
        const Icon = AI_ACTION_ICONS[actionId]
        const isToneAction = actionId === 'changeTone'

        return (
          <div
            key={actionId}
            className="ds-rich-text-editor__ai-item-wrap"
            onMouseEnter={() => setToneMenuOpen(isToneAction)}
          >
            <button
              type="button"
              className={`ds-rich-text-editor__ai-item${
                isToneAction && toneMenuOpen
                  ? ' ds-rich-text-editor__ai-item--open'
                  : ''
              }`}
              role="menuitem"
              aria-haspopup={isToneAction ? 'menu' : undefined}
              aria-expanded={isToneAction ? toneMenuOpen : undefined}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (isToneAction) {
                  setToneMenuOpen((open) => !open)
                  return
                }

                onAction(actionId)
              }}
            >
              <Icon
                className="ds-rich-text-editor__ai-item-icon"
                width={16}
                height={16}
                aria-hidden="true"
              />
              <span>{RICH_TEXT_EDITOR_AI_ACTION_LABELS[actionId]}</span>
              {isToneAction ? (
                <ArrowRightIcon
                  className="ds-rich-text-editor__ai-item-caret"
                  width={16}
                  height={16}
                  aria-hidden="true"
                />
              ) : null}
            </button>

            {isToneAction && toneMenuOpen ? (
              <div
                className="ds-rich-text-editor__ai-submenu"
                role="menu"
                aria-label="Change tone"
              >
                {RICH_TEXT_EDITOR_AI_TONES.map((toneId) => (
                  <button
                    key={toneId}
                    type="button"
                    className="ds-rich-text-editor__ai-item ds-rich-text-editor__ai-item--tone"
                    role="menuitem"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onToneSelect(toneId)}
                  >
                    <span>{RICH_TEXT_EDITOR_AI_TONE_LABELS[toneId]}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
})

RichTextEditorAiMenu.displayName = 'RichTextEditorAiMenu'

export default RichTextEditorAiMenu
